
import React from 'react';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

interface FormulaDetailsStepProps {
  formData: {
    name: string;
    description: string;
  };
  updateFormData: (data: { name: string; description: string }) => void;
}

const detailsSchema = z.object({
  name: z.string().min(3, "Formula name must be at least 3 characters"),
  description: z.string().optional(),
});

export const FormulaDetailsStep = ({ formData, updateFormData }: FormulaDetailsStepProps) => {
  const form = useForm<z.infer<typeof detailsSchema>>({
    resolver: zodResolver(detailsSchema),
    defaultValues: {
      name: formData.name || '',
      description: formData.description || '',
    },
  });
  
  const onSubmit = (data: z.infer<typeof detailsSchema>) => {
    updateFormData({
      name: data.name,
      description: data.description || '',
    });
  };
  
  React.useEffect(() => {
    const subscription = form.watch((value) => {
      if (value.name !== undefined) {
        updateFormData({
          name: value.name,
          description: value.description || '',
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, updateFormData]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Formula Details</h3>
      <p className="text-sm text-muted-foreground">
        Provide basic information about your formula.
      </p>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Formula Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Eco-Friendly Paint Formula" {...field} />
                </FormControl>
                <FormDescription>
                  A clear, descriptive name for your formula
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Describe the purpose and properties of this formula" 
                    className="min-h-[100px]" 
                    {...field} 
                  />
                </FormControl>
                <FormDescription>
                  Optional: Add details about what this formula is used for
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </div>
  );
};
